import { FaroText, FontWeights, grey } from "@faro-lotv/flat-ui";
import { Box, ButtonBase } from "@mui/material";
import { useEffect, useMemo, useRef, type JSX } from "react";
import {
  DAY_AND_MONTH,
  MONTH_AND_YEAR,
  formatDate,
} from "../capture-date-format";
import { RangeRole, cellColors } from "../calendar/calendar-styles";
import { captureLabel } from "../calendar/calendar-utils";
import { CalendarViewProps } from "../calendar/capture-calendar-types";
import { CaptureCountChip } from "../calendar/capture-markers";
import { TimelineRail } from "./timeline-rail";
import {
  computeTimelineRowRoles,
  convertCaptureBucketsToTimelineRows,
} from "./timeline-rows";

/** Height past which the list scrolls, about seven rows, so the popup stays compact */
const LIST_MAX_HEIGHT = "240px";

/** Corner radius of a lone row, and of the outer corners of a selected block of rows */
const LIST_ROW_RADIUS = "4px";

/** Width of the date column, so the weekdays line up */
const LIST_DATE_WIDTH = "72px";

/**
 * @returns A timeline of the days holding captures, newest first, each month opening with a
 * row that picks it whole
 */
export function CaptureDateList({
  stats,
  startDate,
  endDate,
  onPick,
}: CalendarViewProps): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);
  const rows = useMemo(
    () => convertCaptureBucketsToTimelineRows(stats.days),
    [stats.days],
  );
  const roles = useMemo(
    () => computeTimelineRowRoles(rows, startDate, endDate),
    [rows, startDate, endDate],
  );

  // Open on the selection rather than on the most recent captures
  useEffect(() => {
    const list = listRef.current;
    const selected = list?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (list && selected) {
      list.scrollTop = selected.offsetTop - list.clientHeight / 2;
    }
  }, []);

  return (
    <Box
      ref={listRef}
      component="div"
      sx={{
        maxHeight: LIST_MAX_HEIGHT,
        overflowY: "auto",
        // Keep the scrollbar's space so it never covers the counts when it appears
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        scrollbarColor: `${grey[700]} transparent`,
        userSelect: "none",
      }}
    >
      {rows.map(({ date, count, isMonth }, index) => {
        const role = roles[index];
        const isEndpoint = role.isRangeStart || role.isRangeEnd;
        const { background, hover, color } = cellColors(role);
        const muted = isEndpoint ? "inherit" : grey[400];

        return (
          <ButtonBase
            key={`${isMonth}${date.toMillis()}`}
            aria-label={`${isMonth ? formatDate(date, MONTH_AND_YEAR) : date.toISODate()} ${captureLabel(count)}`}
            aria-pressed={role.isSelected}
            onClick={() => onPick(date, isMonth ? "month" : "day")}
            sx={{
              width: "100%",
              height: "32px",
              justifyContent: "flex-start",
              textAlign: "left",
              pr: 1.5,
              borderRadius: listRowRadius(role),
              color,
              backgroundColor: background,
              "&:hover": { backgroundColor: hover },
            }}
          >
            <TimelineRail
              isMonth={isMonth}
              isFirst={index === 0}
              isLast={index === rows.length - 1}
              isSolid={isEndpoint}
            />
            {isMonth ? (
              <>
                <FaroText variant="bodyM" color="inherit">
                  {formatDate(date, { month: "short", year: "numeric" })}
                </FaroText>
                <FaroText variant="bodyS" color={muted} sx={{ ml: "auto" }}>
                  {captureLabel(count)}
                </FaroText>
              </>
            ) : (
              <>
                <FaroText
                  variant="bodyM"
                  color="inherit"
                  sx={{
                    width: LIST_DATE_WIDTH,
                    fontWeight: isEndpoint
                      ? FontWeights.SemiBold
                      : FontWeights.Regular,
                  }}
                >
                  {formatDate(date, DAY_AND_MONTH)}
                </FaroText>
                <FaroText variant="bodyM" color={muted}>
                  {formatDate(date, { weekday: "short" })}
                </FaroText>
                <Box component="span" sx={{ ml: "auto", display: "flex" }}>
                  <CaptureCountChip count={count} />
                </Box>
              </>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/**
 * @returns The corners of a row: rounded alone, or on the outer side of a range end so the
 * rows inside the range join into one block
 * @param role How the row relates to the active range
 */
function listRowRadius({
  isSelected,
  isRangeStart,
  isRangeEnd,
}: RangeRole): string {
  if (!isSelected) return LIST_ROW_RADIUS;
  const top = isRangeEnd ? LIST_ROW_RADIUS : "0";
  const bottom = isRangeStart ? LIST_ROW_RADIUS : "0";
  return `${top} ${top} ${bottom} ${bottom}`;
}
